import { downloadCSV } from 'react-admin';
import type { ClicksByMerchantsResource } from '../../../../Entities/ClicksByMerchant';
import type { MerchantResource } from '../../../../Entities/Merchant';
import type { MerchantStatusResource } from '../../../../Entities/MerchantStatus';
import type { RegionResource } from '../../../../Entities/Region';
import { idFrom, isNonEmptyString, isNumericString } from '../../../../utils';
import { BILLING_TYPE_CHOICES, PAYMENT_TYPE_CHOICES } from '../../../Merchant/utils';
import {
  escapeCsv,
  getReportDateLabels,
  type ReportFilter,
} from '../../../../Components/ReportsWidget/utils';

export interface Filter extends ReportFilter {
  merchant_ids?: (string | number)[];
}

export interface PrefetchedData {
  clicks_by_merchants?: ClicksByMerchantsResource[];
  merchants?: MerchantResource[];
  merchant_statuses?: MerchantStatusResource[];
  regions?: RegionResource[];
}

export function getClicksByMerchantReportParams(filter: Filter) {
  if (!isNonEmptyString(filter.greaterOrEqual?.date)) {
    throw new Error('Clicks by merchant report requires `from` date');
  }
  if (!isNonEmptyString(filter.lessOrEqual?.date)) {
    throw new Error('Clicks by merchant report requires `to` date');
  }
  const regionIds = filter.region_ids?.filter(isNumericString).map(Number);
  const merchantIds = filter.merchant_ids?.filter(isNumericString).map(Number);
  return {
    from: filter.greaterOrEqual.date,
    to: filter.lessOrEqual.date,
    regionIds: regionIds?.length ? regionIds : undefined,
    merchantIds: merchantIds?.length ? merchantIds : undefined,
  };
}

function choiceLabel(choices: readonly { id: number; name: string }[], value: unknown): string {
  if (!isNumericString(value) && (typeof value !== 'number' || !Number.isFinite(value))) {
    return '';
  }

  return choices.find((c) => c.id === Number(value))?.name ?? '';
}

export function downloadClicksByMerchantsCsv(
  records: ClicksByMerchantsResource[],
  prefetched: PrefetchedData,
  filter: Filter,
): void {
  const { startDate, endDate } = getReportDateLabels(filter);

  const header = [
    'Merchant ID',
    'Merchant Name',
    'Region',
    'Start Date',
    'End Date',
    'Status',
    'Payment Type',
    'Billing Type',
    'Is Foreign Company?',
    'Monthly Budget',
    'Total Clicks',
    'Chargeable Clicks',
    'Non-Chargeable Clicks',
    'Bidding Clicks',
    'Getprice Clicks',
    'Future Clicks',
    'Chargeable Income',
    'Non-Chargeable Income',
    'Total CPC Income',
  ].join(',');

  const merchantStatuses = prefetched.merchant_statuses ?? [];

  const rows = records.map((record) => {
    const attrs = record.attributes ?? {};
    const merchant = prefetched.merchants?.find((m) => m.id === idFrom(record, 'merchant'));
    const region = prefetched.regions?.find((r) => r.id === idFrom(record, 'region'));
    const statusName =
      merchantStatuses.find((s) => s.id === idFrom(merchant, 'merchant_status'))?.attributes
        ?.name ?? '';

    return [
      record.relationships?.merchant?.data?.id ?? '',
      escapeCsv(merchant?.attributes?.merchant_name),
      escapeCsv(region?.attributes?.name),
      escapeCsv(startDate),
      escapeCsv(endDate),
      escapeCsv(statusName),
      escapeCsv(choiceLabel(PAYMENT_TYPE_CHOICES, merchant?.attributes?.payment_type_code)),
      escapeCsv(choiceLabel(BILLING_TYPE_CHOICES, merchant?.attributes?.billing_type_code)),
      merchant?.attributes?.is_foreign_company ? 'Yes' : 'No',
      merchant?.attributes?.monthly_budget ?? '',
      attrs.total_clicks ?? '',
      attrs.chargeable_clicks ?? '',
      attrs.non_chargeable_clicks ?? '',
      attrs.bidding_clicks ?? '',
      attrs.getprice_clicks ?? '',
      attrs.future_clicks ?? '',
      attrs.chargeable_income ?? '',
      attrs.non_chargeable_income ?? '',
      attrs.total_cpc_income ?? '',
    ].join(',');
  });

  downloadCSV([header, ...rows].join('\n'), `clicks_by_merchant_${startDate}_through_${endDate}`);
}
